import { twoFactorPolicySchema, type TwoFactorPolicy } from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormAlert } from '@/components/ui/form';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

type Mode = 'none' | 'all' | 'some';

const modeOf = (roles: TwoFactorPolicy['roles'], required: string[]): Mode =>
  required.length === 0 ? 'none' : required.length === roles.length ? 'all' : 'some';

/**
 * Política de verificação em duas etapas da organização (RF-03): ninguém, todos os perfis ou perfis
 * específicos. Quem passa a ser exigido ativa no próximo login, antes de entrar.
 */
export function TwoFactorPolicyPanel({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const key = ['two-factor-policy', organizationId];
  const [draft, setDraft] = useState<string[] | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);
  const [confirming, setConfirming] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const name = useId();

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/two-factor-policy`, {
        schema: twoFactorPolicySchema,
        signal,
      }),
  });

  const content = () => {
    if (query.isPending)
      return <LoadingState rows={2} label="Carregando a política de verificação em duas etapas" />;
    if (query.isError) {
      return (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      );
    }
    const roles = query.data.roles;
    const saved = roles.filter((r) => r.required).map((r) => r.id);
    const required = draft ?? saved;
    const currentMode = mode ?? modeOf(roles, required);
    const readOnly = !can('administracao', 'admin') || saving;
    const dirty = [...required].sort().join() !== [...saved].sort().join();

    const choose = (next: Mode) => {
      setMode(next);
      setSaveError(null);
      setDraft(next === 'none' ? [] : next === 'all' ? roles.map((r) => r.id) : required);
    };
    const toggle = (id: string, on: boolean) => {
      setSaveError(null);
      setDraft(on ? [...required, id] : required.filter((x) => x !== id));
    };

    async function save(ids: string[]) {
      setConfirming(null);
      setSaving(true);
      setSaveError(null);
      try {
        const result = await client.request(`/organizations/${organizationId}/two-factor-policy`, {
          method: 'PUT',
          body: { requiredRoleIds: ids },
          schema: twoFactorPolicySchema,
        });
        queryClient.setQueryData(key, result);
        setDraft(null);
        setMode(null);
        toast.success('Política de verificação em duas etapas salva', {
          description: 'Quem passou a ser exigido ativa no próximo login.',
        });
      } catch (err) {
        setSaveError(err);
      } finally {
        setSaving(false);
      }
    }

    const added = roles.filter((r) => required.includes(r.id) && !saved.includes(r.id)).map((r) => r.name);
    const options: { value: Mode; label: string; hint: string }[] = [
      { value: 'none', label: 'Não exigir', hint: 'Cada pessoa decide se ativa em Minha conta.' },
      { value: 'all', label: 'Exigir de todos', hint: 'Todos os perfis desta organização.' },
      { value: 'some', label: 'Exigir de perfis específicos', hint: 'Escolha os perfis abaixo.' },
    ];

    return (
      <>
        <fieldset disabled={readOnly}>
          <legend className="sr-only">Quem precisa usar a verificação em duas etapas</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {options.map((o) => (
              <label
                key={o.value}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-lg border p-3 text-sm transition-colors',
                  currentMode === o.value ? 'border-brand bg-brand-soft/60' : 'border-border hover:bg-muted',
                  readOnly && 'cursor-default opacity-80',
                )}
              >
                <input
                  type="radio"
                  name={name}
                  className="mt-0.5 size-4 accent-(--brand)"
                  checked={currentMode === o.value}
                  onChange={() => choose(o.value)}
                />
                <span>
                  <span className="block font-semibold">{o.label}</span>
                  <span className="text-muted-foreground">{o.hint}</span>
                </span>
              </label>
            ))}
          </div>
          {currentMode === 'some' ? (
            <ul aria-label="Perfis que exigem" className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {roles.map((r) => (
                <li key={r.id}>
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 accent-(--brand)"
                      checked={required.includes(r.id)}
                      onChange={(e) => toggle(r.id, e.target.checked)}
                    />
                    {r.name}
                  </label>
                </li>
              ))}
            </ul>
          ) : null}
        </fieldset>
        {saveError ? (
          <div className="mt-3">
            <FormAlert tone="error">{errorMessage(saveError)}</FormAlert>
          </div>
        ) : null}
        {can('administracao', 'admin') ? (
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              disabled={!dirty || saving}
              onClick={() => {
                setDraft(null);
                setMode(null);
                setSaveError(null);
              }}
            >
              Descartar
            </Button>
            <Button
              disabled={!dirty}
              loading={saving}
              onClick={() => (added.length ? setConfirming(added) : void save(required))}
            >
              Salvar política
            </Button>
          </div>
        ) : null}
        <ConfirmDialog
          open={confirming !== null}
          onOpenChange={(open) => !open && setConfirming(null)}
          title="Exigir a verificação em duas etapas?"
          description={`Quem tem ${(confirming ?? []).join(', ')} e ainda não usa a verificação vai precisar ativá-la no próximo login, com um app autenticador no celular.`}
          confirmLabel="Exigir"
          busy={saving}
          onConfirm={() => void save(required)}
        />
      </>
    );
  };

  return (
    <section
      aria-label="Verificação em duas etapas"
      className="mt-6 rounded-lg border border-border bg-surface p-5"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-5 text-brand-strong dark:text-brand" aria-hidden />
        <h2 className="text-base font-bold">Verificação em duas etapas</h2>
      </div>
      <p className="mt-1 mb-4 max-w-prose text-sm text-muted-foreground">
        Além da senha, um código do app autenticador do celular. Exigir protege contas com acesso a dados
        sensíveis.
      </p>
      {content()}
    </section>
  );
}
